
import { computed, defineComponent, getCurrentInstance, PropType, ref, toRef } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { getModule } from "vuex-module-decorators";
import CiUserAvatar from "@components/base/avatar.vue";
import CiButton from "@components/base/button.vue";
import CiShare from "@components/base/share.vue";
import AdvisorContactCTA from "@components/users/advisors/advisor-contact-cta.vue";
import { ModalStoreContactForm as ModalStore } from "@components/users/profile/contact-form-modal.store";
import { useI18n } from "@composables/i18n";
import { Modals, SharingNetworks } from "@models/enums";
import { ROUTENAMES } from "@models/constants/routes";
import { ciTrackingService } from "@services/ci-tracking.service";
import { settingsService } from "@services/settings.service";
import { ViewportStoreModule } from "@stores/viewport.store";
import { CurrentUser } from "@utils/user";
import { useUserProfile } from "./userProfileComposable";

const ViewportStore = getModule(ViewportStoreModule);

export default defineComponent({
  name: "CiUserProfileHeader",
  components: {
    "ci-advisor-contact-cta": AdvisorContactCTA,
    "ci-button": CiButton,
    "ci-contact-form-modal": () => import("@components/users/profile/contact-form-modal.vue"),
    "ci-contact-success-modal": () => import("@components/users/profile/contact-success-modal.vue"),
    "ci-share": CiShare,
    "ci-user-avatar": CiUserAvatar,
  },
  props: {
    user: {
      type: Object as PropType<UsersV2.User>,
      default: () => ({}),
    },
  },
  setup(props) {
    const senderEmail = ref("");
    const confirmedGuest = ref(false);
    const { t } = useI18n(getCurrentInstance());
    const root = getCurrentInstance()?.proxy.$root;
    const route = useRoute();
    const router = useRouter();
    const { showPolicies, targetType } = useUserProfile(toRef(props, "user"));

    const actionAttributes: Indexable = computed(() => {
      const avatarSize = ViewportStore.handheld ? "xxl" : "xxxxl";
      return {
        avatar: {
          src: props.user.images.avatar,
          size: avatarSize,
          showVerifiedBadge: props.user.show_verified_badge,
        },
        messaging: {
          gated: !CurrentUser.signedIn,
          icon: "mdi-message-text",
          variant: "filled-primary",
          label: t("ui.views.users.show.send_message"),
        },
        edit: {
          icon: "mdi-pencil",
          variant: "filled-primary",
          label: t("ui.views.users.show.edit_profile"),
          to: { name: ROUTENAMES.USERS_EDIT, params: { id: props.user.slug } },
        },
        contact: {
          icon: "mdi-information",
          label: t("ui.views.users.show.contact"),
        },
        sharing: {
          trackingTarget: {
            name: targetType.value,
            identifier: props.user?.id.toString(),
          },
          url: location.href,
          title: props.user.display_name,
          dropdown: true,
          preventToggle: false,
          type: "user",
          networks: [
            SharingNetworks.CustomEmail,
            SharingNetworks.CustomMessaging,
            SharingNetworks.LinkedIn,
            SharingNetworks.Twitter,
            SharingNetworks.Facebook,
          ],
        },
        privacy: {
          icon: "mdi-cog",
          label: ViewportStore.smartphone ? "" : t("ui.views.users.show.privacy"),
          to: { name: ROUTENAMES.USERS_SETTINGS, query: { tab: "privacy_settings" } },
          tooltip: ViewportStore.smartphone ? t("ui.views.users.show.privacy") : "",
          variant: ViewportStore.smartphone ? "ghost-light" : "outline-light",
        },
      };
    });

    const mounted = () => {
      if (route?.query?.confirmed === "true") {
        confirmedGuest.value = true;
        root!.$bvModal.show(Modals.CONTACT_SUCCESS_MODAL);
        router.replace({ query: {} });
      }
    };

    const openContactForm = () => {
      root!.$bvModal.show(ModalStore.id);
    };

    const getSenderEmail = (value: string) => {
      senderEmail.value = value;
    };

    const handleMessagingClick = async () => {
      if (CurrentUser.signedIn) root!.$bvModal.show(`modal-direct-message--${props.user.id}`);
      else root!.$bvModal.show("modal-login");
      await ciTrackingService.trackClick("ButtonSendMessage", props.user?.id.toString(), targetType.value);
    };

    const handleEditClick = async () => {
      await ciTrackingService.trackClick("ButtonEditUserProfile", props.user?.id.toString(), targetType.value);
    };

    const handlePrivacyClick = async () => {
      await ciTrackingService.trackClick("ButtonEditPrivacySettings", props.user?.id.toString(), targetType.value);
    };

    return {
      actionAttributes,
      senderEmail,
      confirmedGuest,
      mounted,
      openContactForm,
      getSenderEmail,
      handleMessagingClick,
      handleEditClick,
      handlePrivacyClick,
      showPolicies,
    };
  },
  computed: {
    guest(): boolean {
      return settingsService.options.user.type === "guest";
    },
    displayAdvisorCta(): boolean {
      return (
        this.user.type === "advisor" &&
        this.user.id !== CurrentUser.id &&
        this.user.eligible_for_advisor_search === true
      );
    },
    typeMapping(): string {
      const { t } = useI18n(getCurrentInstance());
      if (this.user.type === "advisor" && this.user.advisor_label) {
        return t(`${this.user.advisor_label}`) as string;
      }
      return t(`ui.views.users.show.types.${this.user.label_key}`) as string;
    },
    joinedAddress(): string {
      const userAdress = [this.user.location?.city, this.user.location?.country_name].filter((n) => n).join(", ");
      const postcode = this.user.location?.zipcode || "";
      return `${postcode} ${userAdress}`.trim();
    },
    advisorSearchByLocation(): RawLocation {
      const root = getCurrentInstance()?.proxy.$root;
      const params: Record<string, string> = {};
      if (this.user.location?.city) params.location = root!.$parameterize(this.user.location?.city) as string;
      return { name: ROUTENAMES.ADVISOR_SEARCH_INDEX, params };
    },
    positionAndCompanyName(): string {
      const { profileOwner } = useUserProfile(toRef(this, "user"));
      const { t } = useI18n(getCurrentInstance());
      let { position } = this.user;
      if (!position && profileOwner.value) {
        position = t("ui.views.users.show.no_position_given") as string;
      }
      return [position, this.user.company.name].filter((v) => !!v).join(", ");
    },
    paramCode(): Nullable<string> {
      const route = useRoute();
      return route.query.code as Nullable<string>;
    },
  },
});
